import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { DOCUMENT } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  effect,
  ElementRef,
  inject,
  Injectable,
  Injector,
  OutputEmitterRef,
  Signal,
  signal,
  Type,
} from '@angular/core';
import { Router } from '@angular/router';
import { ToastService } from '@capturum/ui/api';
import { LoadingService } from '@capturum/ui/loader';
import { SESSION_STORAGE } from '@core/configs/token.config';
import { EntityApiActions } from '@core/enums/entities.enum';
import { DEFAULT_FLOW_PRIORITY } from '@core/enums/flow-priority.enum';
import { AppRoutes } from '@core/enums/routes.enum';
import { accumulateOnCtrl, createGrid } from '@core/helpers/flow.helper';
import { flowArrangeLayoutOptions, PositionOnlyApplier } from '@core/retejs/appliers/position-only-applier';
import { SelectableNodes } from '@core/retejs/plugins/marquee-plugin/interfaces/marquee-plugin.interface';
import { MarqueePlugin } from '@core/retejs/plugins/marquee-plugin/marquee-plugin';
import { connectionUtilsProxy, nodeUtilsProxy } from '@core/utils/utils-proxy';
import { environment } from '@environments/environment';
import { Parameter } from '@features/applications/interfaces/action.interface';
import { TranslateService } from '@ngx-translate/core';
import { curveBasis } from 'd3-shape';
import { ConfirmationService } from 'primeng/api';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ClassicPreset, NodeEditor } from 'rete';
import { AngularArea2D, AngularPlugin, NodeComponent, Presets } from 'rete-angular-plugin/21';
import { Position } from '@core/interfaces/position.interface';
import { AreaExtensions, AreaPlugin, NodeView } from 'rete-area-plugin';
import { Presets as ArrangePresets, AutoArrangePlugin } from 'rete-auto-arrange-plugin';
import { ConnectionPathPlugin } from 'rete-connection-path-plugin';
import { Transformer } from 'rete-connection-path-plugin/_types/types';
import { BidirectFlow, ConnectionPlugin } from 'rete-connection-plugin';
import { HistoryActions, HistoryExtensions, HistoryPlugin, Presets as HistoryPresets } from 'rete-history-plugin';
import { MinimapPlugin } from 'rete-minimap-plugin';
import { getDOMSocketPosition } from 'rete-render-utils';
import { structures } from 'rete-structures';
import {
  catchError,
  EMPTY,
  exhaustMap,
  filter,
  finalize,
  from,
  fromEvent,
  interval,
  map,
  merge,
  Subject,
  takeUntil,
  timeout,
} from 'rxjs';
import { CustomConnectionComponent } from '../components/custom-connection/custom-connection.component';
import { CustomSocketComponent } from '../components/custom-socket/custom-socket.component';
import { DefaultInputSocketComponent } from '../components/default-input-socket/default-input-socket.component';
import { DefaultOutputSocketComponent } from '../components/default-output-socket/default-output-socket.component';
import { MagneticConnectionComponent } from '../components/magnetic-connection/magnetic-connection.component';
import { NodeEditorComponent } from '../components/node-editor/node-editor.component';
import { ActionNodeComponent } from '../components/nodes/action-node/action-node.component';
import { AnnotationNodeComponent } from '../components/nodes/annotation-node/annotation-node.component';
import { ConditionNodeComponent } from '../components/nodes/condition-node/condition-node.component';
import { TerminalNodeComponent } from '../components/nodes/terminal-node/terminal-node.component';
import { detectLoopConnections } from '../components/nodes/utils/node.helper';
import {
  AreaExtra,
  Connection,
  Flow,
  FlowGrid,
  FlowNode,
  FlowPayload,
  FlowSaveAsNewVersionPayload,
  FlowState,
  FlowWorkerLoadFlowResult,
  FlowWorkerMessageData,
  FlowWorkerMessageDataType,
  FlowWorkerNode,
  FlowWorkerPostMessageData,
  PendingPathCallbacks,
  ProcessedConnection,
  Schemes,
  ZoomDirection,
} from '../interfaces/flow-ui.interface';
import { JSONValue, NodeData, NodeEditorCondition, NodeType } from '../interfaces/node.interface';
import { FlowApiService } from './flow-api.service';
import { FlowStateService } from './flow-state.service';
import {
  coreNodes,
  CoreNodesEnum,
  lastUnderscoreNodeIndex,
  serializeControl,
  serializeOutputPort,
  serializePort,
  triggerNodes,
  TriggerNodesEnum,
} from './graph.utils';

/**
 * Render-preset payload for a given render kind (`'node'` | `'connection'` | ...).
 * Reconstructs rete-angular-plugin v21's internal `ExtractPayload` (no longer exported publicly)
 * from the exported `AngularArea2D` render-signal union.
 */
type RenderPayload<K extends string> = Extract<AngularArea2D<Schemes>, { type: 'render'; data: { type: K } }>['data'];

@Injectable({
  providedIn: 'root',
})
export class FlowService {
  editor: NodeEditor<Schemes>;
  area: AreaPlugin<Schemes, AreaExtra>;
  render: AngularPlugin<Schemes, AreaExtra>;
  connection: ConnectionPlugin<Schemes, AreaExtra>;
  pathPlugin: ConnectionPathPlugin<Schemes, AreaExtra>;
  socket: ClassicPreset.Socket;
  nodes = signal<FlowNode[]>([]);
  minimap: MinimapPlugin<Schemes>;
  history = new HistoryPlugin<Schemes, HistoryActions<Schemes>>();
  arrange: AutoArrangePlugin<Schemes>;
  zoomLevel = signal<number>(1);
  showResetZoom = signal<boolean>(false);
  sidebarVisible = signal<boolean>(false);
  flowName = signal<string>(null);
  areaExtensions = AreaExtensions;
  nodeEditorElement: ElementRef<HTMLDivElement>;
  editorAreaHolderElement: HTMLElement;
  translateService = inject(TranslateService);
  initialized = signal<boolean>(false);
  isFullscreen = signal<boolean>(false);
  selectableNodes: SelectableNodes;

  grid = signal<FlowGrid>([]);
  flowMaintenanceState = signal(false);
  isLoading = signal(false);
  marqueeEnabled: Signal<boolean>;

  protected worker: Worker;
  protected workers: Worker[] = [];
  protected workerStatus: boolean[] = [];
  protected workerCount = navigator.hardwareConcurrency - 2 || 4;
  protected workerTaskQueue: FlowWorkerMessageData[] = [];

  readonly #injector = inject(Injector);
  readonly #apiService = inject(FlowApiService);
  readonly #dialogService = inject(DialogService);
  readonly #flowStateService = inject(FlowStateService);
  readonly #formlyTypeValidityChange = new OutputEmitterRef<boolean>();
  readonly #router = inject(Router);
  readonly #toastService = inject(ToastService);
  readonly #confirmationService = inject(ConfirmationService);
  readonly #loadingService = inject(LoadingService);
  readonly #sessionStorage = inject(SESSION_STORAGE, { optional: true }) ?? window.sessionStorage;
  readonly #document = inject(DOCUMENT);

  #backgroundImageSize = 20;
  #coreNodes: Record<CoreNodesEnum, NodeData>;
  #triggerNodes: Record<TriggerNodesEnum, NodeData>;
  #deletedNodeIds = new Set<number>();
  #flowLoaded = signal(false);
  #hasUnsavedChanges: Signal<boolean>;
  #minZoom = 0.2;
  #maxZoom = 2;
  #cellSize = 20;
  #nodeSizeCache: Record<string, { width: number; height: number }> = {};
  #layoutInProgress = false;
  #pendingPathCallbacks: Record<string, PendingPathCallbacks> = {};
  #pathCache: Record<string, Position[]> = {};
  #connectionWaypoints: Record<string, Position[]> = {}; // Maps connection ID → waypoints for saving
  #connectionPostMessage: FlowWorkerMessageData;
  readonly #errorLabel = this.translateService.instant('general.error');
  #batchSize = 25;
  #accumulateOnCtrl: { active: () => boolean; destroy: () => void };
  #marqueeState = signal<boolean>(false);
  #flow = signal<Flow>(null);
  readonly #storageKey = 'flow-client-session-id';
  readonly #flowLeaseTokenKey = 'flow-lease-token';
  readonly #flowLeaseTokenDurationKey = 'flow-lease-token-duration';
  readonly #destroyLeaseToken$ = new Subject<void>();
  readonly #defaultLeaseTokenDuration = '60'; // in seconds, string to match sessionStorage type

  flow = this.#flow.asReadonly();

  constructor() {
    this.#coreNodes = coreNodes(this.translateService);
    this.#triggerNodes = triggerNodes(this.translateService);
    this.#hasUnsavedChanges = this.#flowStateService.flowUIStateSelectors.flowUIPropertyValue('hasUnsavedChanges');
    this.marqueeEnabled = this.#marqueeState.asReadonly();

    effect(() => {
      const flowLoaded = this.#flowLoaded();

      if (flowLoaded && environment.enableAStar) {
        this.editor?.getConnections()?.forEach((connection) => {
          if (this.#pendingPathCallbacks[connection.id]) {
            const message = {
              type: FlowWorkerMessageDataType.pathFinding,
              data: {
                points: this.#pendingPathCallbacks[connection.id].points,
                cellSize: this.#cellSize,
                grid: this.grid(),
                translateXY: this.getAreaHolderTranslateXY(),
                nodes: this.#getFlowWorkerNodes(),
              },
              requestId: connection.id,
            };

            this.dispatchToWorker(message);
          }
        });
      }
    });
  }

  triggerFormlyTypeValidity(): void {
    this.#formlyTypeValidityChange.emit(true);
  }

  onTriggerFormlyTypeChange(): OutputEmitterRef<boolean> {
    return this.#formlyTypeValidityChange;
  }

  get lastNodeView(): NodeView {
    return [...this.area.nodeViews.values()].at(-1);
  }

  async loadFlow(flow: Flow): Promise<Flow> {
    this.isLoading.set(true);
    // reset nodes
    this.nodes.set([]);

    const requestId = Date.now().toString();

    return new Promise<Flow>((resolve, reject) => {
      const originalHandler = this.handleWorkerMessage.bind(this);

      this.handleWorkerMessage = (data: FlowWorkerPostMessageData, workIndex: number) => {
        if (data.type === FlowWorkerMessageDataType.loadFlow && data.requestId === requestId) {
          const { processedNodes, processedConnections, updatedFlow } = data.data as FlowWorkerLoadFlowResult;

          // Update UI in batch to prevent UI freeze
          this.#addNodesInBatches(processedNodes) // first add nodes to the editor
            .then(() => {
              // then translate node positions
              return this.#translateInBatches(flow.nodes);
            })
            .then(() => {
              // focus and zoom to fit all nodes
              return this.areaExtensions.zoomAt(this.area, this.editor.getNodes());
            })
            .then(() => {
              // Populate waypoint cache BEFORE adding connections so the
              // path transformer can use stored waypoints on first render
              this.#populatePathCacheFromLoadedWaypoints(processedConnections);

              // then add connections
              return this.#addConnectionsInBatches(processedConnections);
            })
            .then(() => {
              this.#updateFlowGrid();
              this.history.clear();

              return from(this.#flowStateService.flowUIStateActions.setFlowValue(updatedFlow));
            })
            .then(() => {
              return new Promise((resolve) => {
                setTimeout(resolve, 0);
              });
            })
            .then(() => {
              this.#flowLoaded.set(true);
              this.isLoading.set(false);
              this.#flow.set(updatedFlow);

              resolve(updatedFlow);
            })
            .catch((err) => {
              this.isLoading.set(false);
              reject(err);
            });
        } else {
          originalHandler(data, workIndex);
        }
      };

      const messageData: FlowWorkerMessageData = {
        type: FlowWorkerMessageDataType.loadFlow,
        requestId,
        data: {
          flow,
          coreNodes: this.#coreNodes,
          triggerNodes: this.#triggerNodes,
          errorLabel: this.#errorLabel,
        },
      };

      this.dispatchToWorker(messageData);
    });
  }

  configureEditor(nodeEditor: ElementRef): void {
    this.nodeEditorElement = nodeEditor;

    this.#initWorkerPool();
    this.#initEditor(nodeEditor);
    this.#initAreaExtensions();
    this.#initPlugins();
    this.#handleNodeClick();
  }

  async layoutGraph(): Promise<void> {
    const nodes = this.editor?.getNodes() ?? [];

    if (!nodes.length || this.flow()?.readonly) {
      return;
    }

    const arrangeableNodes = nodes.filter((node) => {
      return node.nodeData.type.toLowerCase() !== NodeType.Annotation.toLowerCase();
    });

    if (!arrangeableNodes.length) {
      return;
    }

    const arrangeableNodeIds = new Set(arrangeableNodes.map((node) => node.id));
    const arrangeableConnections = this.editor.getConnections().filter((connection) => {
      return arrangeableNodeIds.has(connection.source) && arrangeableNodeIds.has(connection.target);
    });

    this.#applyMeasuredNodeSizes(arrangeableNodes);

    this.#layoutInProgress = true;
    this.history.separate();

    try {
      await this.arrange.layout({
        nodes: arrangeableNodes,
        connections: arrangeableConnections,
        applier: new PositionOnlyApplier<Schemes, never>(),
        options: flowArrangeLayoutOptions,
      });
    } finally {
      this.#layoutInProgress = false;
      this.history.separate();
    }

    if (this.grid()?.length) {
      this.#updateFlowGrid();
    }

    setTimeout(() => {
      this.areaExtensions.zoomAt(this.area, nodes);
    });
  }

  async redo(): Promise<void> {
    await this.history.redo();
  }

  async undo(): Promise<void> {
    await this.history.undo();
  }

  setInitialized(value: boolean): void {
    this.initialized.set(value);
  }

  createNode(nodeConfig: NodeData): FlowNode {
    this.setHasUnsavedChanges();

    let customElement;
    const node = new FlowNode(nodeConfig.name);

    // BE doesn't accept string id, so we need to convert it to number
    // to mark new node BE needs a negative number
    // check if there is already a created node
    let id = parseInt(this.editor.getNodes().slice(-1)[0]?.id || '0', 10);
    const latestCreatedNode = this.editor.getNodes().some((node) => {
      return Math.sign(+node.id) === -1;
    });

    id = latestCreatedNode
      ? Math.min(
          ...this.editor.getNodes().map((node) => {
            return +node.id;
          }),
        ) - 1
      : -1;

    if (this.#deletedNodeIds.size > 0) {
      const highestDeletedNodeId = Math.min(id, ...this.#deletedNodeIds);

      id = highestDeletedNodeId - 1;
    }

    while ((customElement = customElements.get(`node-${id.toString()}`)) && customElement) {
      id--;
    }

    node.id = id.toString();
    node.nodeData = {
      ...nodeConfig,
      name: (() => {
        switch (nodeConfig.type) {
          case NodeType.Condition:
          case NodeType.Loop:
          case NodeType.Wait:
          case NodeType.Switch:
            return null;
          default:
            return nodeConfig.name;
        }
      })(),
      execution: {
        executed: false,
        result: signal<JSONValue>(null),
      },
    };

    switch (nodeConfig.type) {
      case NodeType.HttpAction: {
        const httpNodes = this.editor.getNodes().filter((node) => {
          return (
            node.nodeData.type === NodeType.HttpAction &&
            (node.label === nodeConfig.name || node.nodeData?.data?.name === nodeConfig.name)
          );
        });

        if (!httpNodes.length) {
          break;
        }

        const nodeSuffixRegex = new RegExp(`^${nodeConfig.name}_\\d+$`);
        const suffixNodes = httpNodes.filter((node) => {
          return nodeSuffixRegex.test(node.nodeData.name);
        });
        const suffixNodeIndex = suffixNodes.length ? lastUnderscoreNodeIndex(suffixNodes) : 0;

        if (!suffixNodeIndex && httpNodes.length === 1) {
          node.nodeData.name = `${nodeConfig.name}_1`;
          break;
        }

        node.nodeData.name = suffixNodeIndex ? `${nodeConfig.name}_${suffixNodeIndex + 1}` : nodeConfig.name;

        break;
      }
      default:
        break;
    }

    nodeConfig.inputs?.forEach((input, index) => {
      node.addInput(
        `port_${index}`,
        new ClassicPreset.Input(this.socket, input.label || '', input.multipleConnections ?? false),
      );
    });

    nodeConfig.outputs?.forEach((output, index) => {
      const key = node.nodeData.type === NodeType.Switch ? `port_default` : `port_${index}`;

      node.addOutput(
        key,
        new ClassicPreset.Output(this.socket, output.label || '', output.multipleConnections ?? false),
      );
    });

    this.nodes.update((nodes) => {
      return [...nodes, node];
    });

    return node;
  }

  async addNewNode(nodeConfig: NodeData): Promise<void> {
    const newNode = this.createNode(nodeConfig);

    // Set node position based on the last added one
    const editorBounds = this.area.container.getBoundingClientRect();
    const offset = 80;
    const connectionOffset = 50;
    const { x, y } = {
      x: editorBounds.width / 4 - offset,
      y: editorBounds.height / 2 - offset,
    };
    const nodeX = this.lastNodeView
      ? this.lastNodeView.position.x + this.lastNodeView.element.clientWidth + connectionOffset
      : x;
    const nodeY = this.lastNodeView ? this.lastNodeView.position.y : y;

    await this.editor.addNode(newNode);
    await this.area.translate(newNode.id, { x: nodeX, y: nodeY });

    this.#updateFlowGrid();

    this.sidebarVisible.set(false);

    setTimeout(() => {
      this.areaExtensions.zoomAt(this.area, this.editor.getNodes());
      this.area.area.zoom(this.zoomLevel());
    }, 10);
  }

  async dropNewNode(node: CdkDragDrop<any, any, NodeData>): Promise<void> {
    const newNode = this.createNode(node.item.data);
    const { z } = this.getAreaHolderTranslateXY();

    const editorBounds = this.editorAreaHolderElement.getBoundingClientRect();
    const nodeX = (node.dropPoint.x - editorBounds.left) / z;
    const nodeY = (node.dropPoint.y - editorBounds.top) / z;

    await this.editor.addNode(newNode);
    await this.area.translate(newNode.id, { x: nodeX, y: nodeY });

    this.#updateFlowGrid();

    this.sidebarVisible.set(false);

    setTimeout(() => {
      this.area.area.zoom(this.zoomLevel());
    }, 10);
  }

  editNode(node: FlowNode, appendTo: string | HTMLElement = 'body'): void {
    this.openFlowDialogComponent<NodeEditorComponent>(NodeEditorComponent, {
      showHeader: false,
      // Esc is handled inside NodeEditorComponent so it runs the same close/save
      // path as the X button (PrimeNG's own Esc-close would bypass it).
      closeOnEscape: false,
      appendTo,
      styleClass: 'node-editor-dialog',
      maskStyleClass: 'node-editor-dialog-mask',
      data: {
        node,
      },
      inputValues: {
        flowService: this,
      },
    });
  }

  async updateNode(node: FlowNode, pristine = false): Promise<void> {
    switch (node.nodeData.type) {
      case NodeType.Switch: {
        const cases = (node.nodeData.parameters as NodeEditorCondition[]) || [];
        const casesKeys = cases?.map((c) => {
          return c.id;
        });

        if (!pristine) {
          const nodeOutputs = Object.entries(node.outputs);
          const toRemoveOutputs = nodeOutputs.filter(([key, output]) => {
            return output.label !== 'default';
          });

          // remove all outputs which id not default
          // default should remain
          // you can't update output label, so we need to remove all outputs and add them again
          for (const [key, _] of toRemoveOutputs) {
            node.removeOutput(key);
          }

          // add new outputs
          for (const switchCase of cases) {
            const output = new ClassicPreset.Output(
              this.socket,
              switchCase.value,
              environment.flow.output.multipleConnections ?? false,
            );

            if (!node.outputs[switchCase.id]) {
              node.addOutput(switchCase.id, output);
            }
          }
        }

        // default output should be at the end of outputs
        // so we need to sort outputs
        const sortedOutputs = Object.entries(node.outputs).sort((a, b) => {
          if (a[0] === 'port_default') {
            return 1;
          }

          if (b[0] === 'port_default') {
            return -1;
          }
        });

        node.outputs = Object.fromEntries(sortedOutputs);

        // get node connections
        const nodeConnections = nodeUtilsProxy.getNodeConnections(this.editor, node.id);
        const toRemoveConnections = nodeConnections.filter((connection) => {
          return (
            !cases.some((c) => {
              return c.id === connection.sourceOutput;
            }) &&
            connection.source === node.id &&
            connection.sourceOutput !== 'port_default'
          );
        });

        // remove connected connections
        if (nodeConnections.length && toRemoveConnections.length) {
          for (const connection of toRemoveConnections) {
            await this.editor.removeConnection(connection.id);
          }
        }

        break;
      }
      default:
        break;
    }

    await this.area.update('node', node.id);
  }

  async removeNode(toRemoveNode: FlowNode): Promise<void> {
    this.setHasUnsavedChanges();

    const connections = nodeUtilsProxy.getNodeConnections(this.editor, toRemoveNode.id);

    for (const connection of connections) {
      await this.editor.removeConnection(connection.id);
    }

    await this.editor.removeNode(toRemoveNode.id);

    // always convert to negative number in order to avoid conflicts with BE
    this.#deletedNodeIds.add(Math.abs(+toRemoveNode.id) * -1);
    this.nodes.set(this.editor.getNodes());
  }

  async removeConnection(connection: Connection<FlowNode>): Promise<boolean> {
    this.setHasUnsavedChanges();

    return await this.editor.removeConnection(connection.id);
  }

  saveFlow(name: string, method: EntityApiActions): void {
    this.#loadingService.toggleLoading(true);
    const isUpdate = method === EntityApiActions.update;
    const saveMethod = isUpdate ? 'updateFlow' : 'saveFlow';
    let data: any = {
      name,
      nodes: [],
      connections: structuredClone(this.editor.getConnections()),
    };
    const nodes = this.editor.getNodes();

    for (const node of nodes) {
      const inputsEntries = Object.entries(node.inputs).map(([key, input]) => {
        return [key, input && serializePort(input)];
      });
      const outputsEntries = Object.entries(node.outputs).map(([key, output]) => {
        return [key, output && serializeOutputPort(key, output, node.nodeData.type, node.nodeData?.kind)];
      });
      const controlsEntries = Object.entries(node.controls).map(([key, control]) => {
        return [key, control && serializeControl(control)];
      });
      const nodeView = this.area.nodeViews.get(node.id);

      data.nodes.push({
        ...node,
        id: node.id,
        label: node.label,
        position: nodeView.position,
        outputs: Object.fromEntries(outputsEntries),
        inputs: Object.fromEntries(inputsEntries),
        controls: Object.fromEntries(controlsEntries),
      });
    }

    const flowValue: Partial<Flow> = this.#flowStateService.flowUIStateSelectors.flowUIValue() || {};

    data = {
      ...flowValue,
      ...data,
      name,
      // A flow saved before its settings tab was touched has no priority in state yet.
      priority: flowValue.priority ?? DEFAULT_FLOW_PRIORITY,
    };

    // remove unnecessary data for payload
    data = this.#prepareFlowData(data);

    this.#apiService
      [saveMethod](data as Flow)
      .pipe(
        catchError((error) => {
          const message = error.error?.error || this.translateService.instant('toast.error.message');

          this.#toastService.error(this.translateService.instant('toast.error.title'), message);

          return EMPTY;
        }),
        finalize(() => {
          this.#loadingService.hideLoader();
        }),
      )
      .subscribe((flow) => {
        const translationKey = `toast.success.${isUpdate ? 'update' : 'create'}`;
        const entity = this.translateService.instant('flow.entity-name');

        this.#toastService.success(
          this.translateService.instant('general.success'),
          this.translateService.instant(translationKey, { entity }),
        );

        // only if it's a new flow, navigate to the flow edit page
        if (!isUpdate) {
          this.#router.navigate([AppRoutes.flow, flow.id], {
            onSameUrlNavigation: 'reload',
          });
        }
      });
  }

  saveAsNewVersion(id: string | number, payload: FlowSaveAsNewVersionPayload): void {
    this.#loadingService.toggleLoading(true);

    this.#apiService
      .saveAsNewVersion(id, payload)
      .pipe(
        catchError((error) => {
          const message = error.error?.error || this.translateService.instant('toast.error.message');

          this.#toastService.error(this.translateService.instant('toast.error.title'), message);

          return EMPTY;
        }),
        finalize(() => {
          this.#loadingService.hideLoader();
        }),
      )
      .subscribe((flow) => {
        const entity = this.translateService.instant('flow.entity-name');

        this.#toastService.success(
          this.translateService.instant('general.success'),
          this.translateService.instant('toast.success.update', { entity }),
        );

        const key = id.toString();

        this.#flowStateService.flowUIStateActions.setFlowValueByKey(key, {
          version: flow.version,
          is_latest: flow.is_latest,
          version_message: undefined,
        });
      });
  }

  createDefaultNodes(): () => void {
    return () => {
      return this.area.destroy();
    };
  }

  terminateAllWorkers(): void {
    this.workers.forEach((worker) => worker.terminate());
    this.workers = [];
    this.workerStatus = [];
    this.workerTaskQueue = [];
  }

  async destroyEditor(): Promise<void> {
    for (const [dialogRef, _] of this.#dialogService.dialogComponentRefMap) {
      dialogRef?.destroy();
    }

    this.area?.destroy();
    this.#accumulateOnCtrl?.destroy();
    this.#flowLoaded.set(false);
    this.#backgroundImageSize = 20;
    this.setMarqueeState(false);
    this.#destroyLeaseToken$.next();

    this.#nodeSizeCache = {};
    this.#pendingPathCallbacks = {};
    this.#connectionPostMessage = null;
    this.#pathCache = {};
    this.#connectionWaypoints = {};
    this.grid.set([]);

    this.terminateAllWorkers();

    await this.editor?.clear();
  }

  resetZoom(): void {
    this.#backgroundImageSize = 20;
    this.area.container.style.removeProperty('background-image');
    this.area.container.style.removeProperty('background-size');
    this.area.area.zoom(1);
  }

  getSelectedNodes(): FlowNode[] {
    return this.editor.getNodes().filter((node) => {
      return node.selected;
    });
  }

  getConnectedNodes(nodeId: string, filtered = true): FlowNode[] {
    const graph = structures(this.editor);
    const filteredFn = filtered
      ? (_, connection) => {
          return !connection.isLoop && connection.source !== nodeId;
        }
      : Boolean;

    return graph.predecessors(nodeId, filteredFn)?.nodes() || [];
  }

  zoom(direction: ZoomDirection): void {
    const zoomStep = direction === 'in' ? 0.1 : -0.1;
    const newZoomLevel = this.zoomLevel() + zoomStep;

    if (newZoomLevel > this.#minZoom && newZoomLevel < this.#maxZoom) {
      this.#setNodeEditorStyles(direction);
    }

    this.area.area.zoom(newZoomLevel);
  }

  openFlowDialogComponent<C = any>(component: Type<C>, config: DynamicDialogConfig): DynamicDialogRef {
    return this.#dialogService.open(component, config);
  }

  openConfirmDialog(
    header: string,
    message: string,
    onAcceptFn: () => void,
    onRejectFn?: () => void,
  ): ConfirmationService {
    return this.#confirmationService.confirm({
      header,
      message,
      accept: onAcceptFn,
      ...(onRejectFn && { reject: onRejectFn }),
    });
  }

  setHasUnsavedChanges(): void {
    if (!this.#hasUnsavedChanges()) {
      this.#flowStateService.flowUIStateActions.setFlowValue({
        hasUnsavedChanges: true,
      });
    }
  }

  getAreaHolderTranslateXY(): { x: number; y: number; z: number } {
    if (!this.editorAreaHolderElement) {
      return { x: 0, y: 0, z: 1 };
    }

    const { transform } = this.area.area;

    return {
      x: transform.x,
      y: transform.y,
      z: transform.k,
    };
  }

  getFreeWorkerIndex(): number | null {
    return this.workerStatus.findIndex((status) => {
      return !status;
    });
  }

  dispatchToWorker(message: FlowWorkerMessageData): void {
    const index = this.getFreeWorkerIndex();

    if (index !== -1) {
      this.workerStatus[index] = true;
      this.workers[index].postMessage(message);
    } else {
      this.workerTaskQueue.push(message);
    }
  }

  processWorkerQueue(): void {
    const index = this.getFreeWorkerIndex();

    if (index !== -1 && this.workerTaskQueue.length > 0) {
      const message = this.workerTaskQueue.shift();

      this.workerStatus[index] = true;
      this.workers[index].postMessage(message);
    }
  }

  handleWorkerMessage(data: FlowWorkerPostMessageData, workerIndex: number): void {
    switch (data.type) {
      case FlowWorkerMessageDataType.grid: {
        this.grid.set(data.data as FlowGrid);
        break;
      }
      case FlowWorkerMessageDataType.pathFinding: {
        const { requestId, ['data']: positions } = data;

        if (requestId && this.#pendingPathCallbacks[requestId]) {
          this.#pendingPathCallbacks[requestId].callback(positions as Position[], requestId);

          delete this.#pendingPathCallbacks[requestId];
        }
        break;
      }
      default:
        break;
    }
  }

  setMarqueeState(state: boolean): void {
    this.#marqueeState.set(state);
  }

  extendLockLeaseToken(id: string | number): void {
    const leaseTokenDuration =
      this.#sessionStorage.getItem(`${this.#flowLeaseTokenDurationKey}-${id}`) || this.#defaultLeaseTokenDuration;
    const timerInterval = Math.floor((parseInt(leaseTokenDuration, 10) * 1000) / 2);
    // A tab Chrome throttles/freezes misses its interval ticks, so the lease can already be
    // expired by the time it wakes up; renew immediately when it becomes visible again too.
    const becameVisible$ = fromEvent(this.#document, 'visibilitychange').pipe(
      filter(() => this.#document.visibilityState === 'visible'),
    );
    const stopHeartbeat$ = new Subject<void>();

    merge(interval(timerInterval), becameVisible$)
      .pipe(
        // exhaustMap (not switchMap): a slow extend must not be cancelled by the next tick,
        // and the error handling lives inside it so one failed tick doesn't end the heartbeat.
        exhaustMap(() => {
          return this.#apiService.extendLockLease(id).pipe(
            // A hung request must not block every later tick forever; a timeout falls into the
            // same "log and keep ticking" branch as any other non-lock-loss error below.
            timeout(timerInterval),
            catchError((error: HttpErrorResponse) => {
              if (error?.status === 410) {
                // Lease already expired: re-acquire the same way a fresh page load would.
                // getFlow stores the new lease token/duration itself; nothing else to do here.
                return this.#apiService.getFlow(id).pipe(
                  map(() => null),
                  catchError((reAcquireError: HttpErrorResponse) => {
                    if (reAcquireError?.status === 409 || reAcquireError?.status === 423) {
                      this.#markFlowLockLost(id, stopHeartbeat$);
                    } else {
                      console.error('Error re-acquiring flow lock:', reAcquireError);
                    }

                    return EMPTY;
                  }),
                );
              }

              if (error?.status === 403) {
                this.#markFlowLockLost(id, stopHeartbeat$);
              } else {
                console.error('Error extending lease token:', error);
              }

              return EMPTY;
            }),
          );
        }),
        takeUntil(merge(this.#destroyLeaseToken$, stopHeartbeat$)),
      )
      .subscribe((response) => {
        if (!response) {
          return;
        }

        const newLeaseToken = response.headers.get('x-lease-token');
        const newLeaseTokenDuration = response.headers.get('x-lease-token-duration') || this.#defaultLeaseTokenDuration;

        this.#sessionStorage.setItem(`${this.#flowLeaseTokenKey}-${id}`, newLeaseToken);
        this.#sessionStorage.setItem(`${this.#flowLeaseTokenDurationKey}-${id}`, newLeaseTokenDuration);
      });
  }

  // Lock taken by someone else (extend 403, or re-acquire 409/423): mirror the read-only state
  // an initial locked-flow load would get, and stop sending extend requests for a lock we no longer hold.
  #markFlowLockLost(id: string | number, stopHeartbeat$: Subject<void>): void {
    this.#flowStateService.flowUIStateActions.setFlowValueByKey(String(id), { readonly: true });
    this.#flow.update((flow) => {
      return flow ? { ...flow, readonly: true } : flow;
    });

    this.#toastService.warning(
      this.translateService.instant('flow.entity-name'),
      this.translateService.instant('flow.read-only-warning'),
    );

    stopHeartbeat$.next();
    stopHeartbeat$.complete();
  }

  protected createAStarTransformer(
    connection: ClassicPreset.Connection<ClassicPreset.Node, ClassicPreset.Node> & {
      isPseudo?: boolean;
      isMagnetic?: boolean;
    },
  ): Transformer {
    return (points: { x: number; y: number }[]) => {
      const connectionFound = this.editor.getConnection(connection.id);

      if (connection.isMagnetic || connection.isPseudo || !connectionFound) {
        return points;
      }

      // Check for pre-loaded waypoints from backend (keyed by connection ID as string)
      const connectionId = String(connection.id);

      if (this.#pathCache[connectionId]) {
        const loadedWaypoints = this.#pathCache[connectionId];
        // Move to points-based cache and remove ID-based entry
        const cacheKeyForPoints = JSON.stringify(points);

        this.#pathCache[cacheKeyForPoints] = loadedWaypoints;
        delete this.#pathCache[connectionId];

        return loadedWaypoints;
      }

      const cacheKey = JSON.stringify(points);

      if (this.#pathCache[cacheKey]) {
        return this.#pathCache[cacheKey];
      }

      const requestId = connection.id;

      const canvasWidth = this.nodeEditorElement.nativeElement.offsetWidth;
      const canvasHeight = this.nodeEditorElement.nativeElement.offsetHeight;
      const cellSize = this.#cellSize;

      if (!this.grid()?.length) {
        this.grid.set(
          createGrid(
            this.editor
              .getNodes()
              .filter((node) => {
                return node.nodeData.type.toLowerCase() !== NodeType.Annotation.toLowerCase();
              })
              .map((node) => {
                const nodeView = this.area.nodeViews.get(node.id);

                if (!nodeView) {
                  return null;
                }

                return {
                  id: node.id,
                  position: nodeView.position,
                  width: nodeView.element.clientWidth,
                  height: nodeView.element.clientHeight,
                  left: nodeView.position.x,
                  top: nodeView.position.y,
                };
              }),
            canvasWidth,
            canvasHeight,
            cellSize,
            this.getAreaHolderTranslateXY(),
          ),
        );
      }

      this.#pendingPathCallbacks[requestId] = {
        points,
        callback: (workerPath: Position[], requestId: string | number) => {
          if (workerPath?.length) {
            this.#pathCache[cacheKey] = workerPath;
            this.#connectionWaypoints[String(requestId)] = workerPath;

            // Trigger a redraw of the area/connections
            this.area?.update?.('connection', requestId as string);
          }
        },
      };

      this.#connectionPostMessage = {
        type: FlowWorkerMessageDataType.pathFinding,
        requestId,
        data: {
          points,
          grid: this.grid(),
          nodes: this.#getFlowWorkerNodes(),
        },
      };

      return points;
    };
  }

  #initWorkerPool(): void {
    for (let i = 0; i < this.workerCount; i++) {
      const worker = new Worker(new URL('./../../../flow-worker.worker', import.meta.url), { type: 'module' });

      worker.onmessage = ({ data }: { data: FlowWorkerPostMessageData }) => {
        this.workerStatus[i] = false;
        this.handleWorkerMessage(data, i);
        this.processWorkerQueue();
      };

      this.workers.push(worker);
      this.workerStatus.push(false);
    }
  }

  #updateNodeSizeCache(nodeId: string) {
    const nodeView = this.area.nodeViews.get(nodeId);

    if (nodeView) {
      this.#nodeSizeCache[nodeId] = {
        width: nodeView.element?.clientWidth,
        height: nodeView.element?.clientHeight,
      };
    }
  }

  /**
   * `FlowNode` carries fallback dimensions that match no renderer. elk lays out on those numbers, so the
   * rendered sizes are measured first and copied onto the nodes; unmeasured nodes keep their fallback.
   */
  #applyMeasuredNodeSizes(nodes: FlowNode[]): void {
    nodes.forEach((node) => {
      this.#updateNodeSizeCache(node.id);

      const measuredSize = this.#nodeSizeCache[node.id];

      if (measuredSize?.width) {
        node.width = measuredSize.width;
      }

      if (measuredSize?.height) {
        node.height = measuredSize.height;
      }
    });
  }

  #updateFlowGrid(): void {
    const nodes = this.editor
      ?.getNodes()
      ?.filter((node) => {
        return node.nodeData.type.toLowerCase() !== NodeType.Annotation.toLowerCase();
      })
      .map((node) => {
        this.#updateNodeSizeCache(node.id);

        return {
          id: node.id,
          position: this.area.nodeViews.get(node.id)?.position,
          width: this.#nodeSizeCache[node.id]?.width ?? 90,
          height: this.#nodeSizeCache[node.id]?.height ?? 90,
        };
      });
    const canvasWidth = this.nodeEditorElement?.nativeElement.offsetWidth;
    const canvasHeight = this.nodeEditorElement?.nativeElement.offsetHeight;
    const message: FlowWorkerMessageData = {
      type: FlowWorkerMessageDataType.grid,
      data: {
        nodes,
        canvasWidth,
        canvasHeight,
        cellSize: this.#cellSize,
        translateXY: this.getAreaHolderTranslateXY(),
      },
    };

    this.dispatchToWorker(message);
  }

  #initEditor(nodeEditor: ElementRef): void {
    this.editor = new NodeEditor<Schemes>();
    this.socket = new ClassicPreset.Socket('socket');
    this.area = new AreaPlugin<Schemes, AreaExtra>(nodeEditor.nativeElement);
    this.render = new AngularPlugin<Schemes, AreaExtra>({ injector: this.#injector });
    this.connection = new ConnectionPlugin<Schemes, AreaExtra>();
    this.pathPlugin = new ConnectionPathPlugin<Schemes, AreaExtra>({
      curve: () => {
        return curveBasis;
      },
      transformer: (connection: ClassicPreset.Connection<ClassicPreset.Node, ClassicPreset.Node>) => {
        // If stored waypoints exist from the backend, use them directly — skip A* entirely
        const storedWaypoints = this.#connectionWaypoints[String(connection.id)];

        if (storedWaypoints?.length) {
          return () => storedWaypoints;
        }

        if (environment.enableAStar) {
          return this.createAStarTransformer(connection);
        }

        return (points: { x: number; y: number }[]) => {
          return points;
        };
      },
    });
    this.editorAreaHolderElement = this.area.area.content.holder;

    this.connection.addPreset(() => {
      const bidirectFlow = new BidirectFlow({
        pickByClick: true,
      });

      // original pick method
      const originalPick = bidirectFlow.pick.bind(bidirectFlow);

      bidirectFlow.pick = async (params, context) => {
        if (!environment.flow.output.multipleConnections && this.#isSocketConnected(params.socket)) {
          return Promise.resolve();
        }

        return originalPick(params, context);
      };

      return bidirectFlow;
    });
    this.editor.use(this.area);
    // @ts-ignore
    this.render.use(this.pathPlugin);
    this.area.use(this.connection);
    this.area.use(this.render);

    this.area.area.setZoomHandler(null);

    this.#initMagneticConnection();

    this.area.addPipe((context) => {
      switch (context.type) {
        case 'connectioncreated':
        case 'nodetranslated':
          if (!this.#hasUnsavedChanges() && this.initialized()) {
            this.setHasUnsavedChanges();
          }

          if (this.grid()?.length && context.type === 'nodetranslated') {
            this.#pathCache = {}; // Clear cached waypoints — paths are stale after node move
            this.#connectionWaypoints = {};

            // An auto-arrange translates every node at once; `layoutGraph` rebuilds the grid once at the end.
            if (!this.#layoutInProgress) {
              this.#updateFlowGrid();
            }
          }

          break;
        case 'zoomed': {
          const zoomLevel = context.data.zoom;
          const zoomed = zoomLevel !== 1;

          if (zoomed && !this.#flowLoaded()) {
            const level = zoomLevel - 1;

            this.#setNodeEditorStyles(undefined, level);
          }

          this.zoomLevel.set(zoomLevel);
          this.showResetZoom.set(zoomed);
          break;
        }
        default:
          break;
      }

      return context;
    });

    this.render.addPipe((context) => {
      if (!context || typeof context !== 'object' || !('type' in context)) {
        return context;
      }

      const graph = structures(this.editor);

      if (context.type === 'connectioncreated' || context.type === 'nodedragged') {
        Object.keys(this.#pendingPathCallbacks).forEach((requestId) => {
          const postMessage = {
            type: FlowWorkerMessageDataType.pathFinding,
            requestId,
            data: {
              grid: this.grid(),
              points: this.#pendingPathCallbacks[requestId].points,
              cellSize: this.#cellSize,
              translateXY: this.getAreaHolderTranslateXY(),
              nodes: this.#getFlowWorkerNodes(),
            },
          };

          this.dispatchToWorker(postMessage);
        });

        this.#connectionPostMessage = null;
      }

      switch (context.type) {
        case 'connectioncreated': {
          const connections = this.editor.getConnections().map((connection) => ({
            from: connection.source,
            to: connection.target,
            connection,
          }));

          detectLoopConnections(connections);

          break;
        }

        case 'connectionpath': {
          const isLoopCondition =
            context.data.payload.target && context.data.payload.targetInput && context.data.payload.isLoop;
          const hasLoopPredecessor = graph
            ?.predecessors(context.data.payload.source)
            .filter((node) => {
              return node.nodeData.type === NodeType.Loop;
            })
            .nodes()?.length;
          const loopIncomer = graph?.incomers(context.data.payload.target).nodes();

          if (
            (isLoopCondition && !hasLoopPredecessor) ||
            (isLoopCondition && hasLoopPredecessor > 0 && loopIncomer?.length <= 1)
          ) {
            context.data.payload.isLoop = false;
          }

          if (!environment.enableAStar) {
            const { points } = context.data;

            if (isLoopCondition && hasLoopPredecessor) {
              return {
                ...context,
                data: {
                  ...context.data,
                  path: nodeUtilsProxy.loopConnectionPath(points as [Position, Position], 0.5, 80),
                },
              };
            } else {
              return {
                ...context,
                data: {
                  ...context.data,
                  path: nodeUtilsProxy.classicConnectionPath(points as [Position, Position], 0.3),
                },
              };
            }
          }
          break;
        }

        default:
          break;
      }

      return context;
    });
  }

  #initPlugins(): void {
    this.render.addPreset(this.#getRenderPreset());
    this.render.addPreset(Presets.minimap.setup({ size: 120 }));
    this.minimap = new MinimapPlugin<Schemes>({
      boundViewport: true,
    });
    this.render.addPreset(
      Presets.classic.setup({
        socketPositionWatcher: getDOMSocketPosition({
          offset({ x, y }, nodeId, side) {
            return { x, y };
          },
        }),
      }),
    );
    this.area.use(this.minimap);

    this.arrange = new AutoArrangePlugin<Schemes>();
    this.history = new HistoryPlugin<Schemes, HistoryActions<Schemes>>();

    this.arrange.addPreset(ArrangePresets.classic.setup());
    this.history.addPreset(HistoryPresets.classic.setup());
    // Without this the plugin has no parent scope and every `arrange.layout()` throws
    // `cannot find parent` from rete's `Scope.parentScope`, which is why auto-arrange has
    // never actually run despite being constructed and given a preset.
    this.area.use(this.arrange);
    this.area.use(this.history);
    const marqueePlugin = new MarqueePlugin({
      container: this.area.container,
      selectableNodes: this.selectableNodes,
      enabled: () => {
        return this.marqueeEnabled();
      },
      deleteConfirm: (done) => {
        const selectedNodes = this.getSelectedNodes();
        const entity = this.translateService.instant('node.label', {
          count: selectedNodes.length,
        });
        const header = this.translateService.instant('confirm.entity.delete.header', {
          entity,
        });
        const message = this.translateService.instant('confirm.entity.delete.confirmation', {
          entity: entity.toLowerCase(),
        });

        this.openConfirmDialog(header, message, async () => {
          for (const node of selectedNodes) {
            await this.removeNode(node);
          }

          await done();
        });
      },
    });

    this.area.use(marqueePlugin);
  }

  #getRenderPreset() {
    return Presets.classic.setup({
      customize: {
        node: (node: RenderPayload<'node'>) => {
          const nodeType = node.payload.nodeData.type?.toLowerCase();

          switch (nodeType) {
            case NodeType.Start:
            case NodeType.End:
            case NodeType.Terminate:
            case NodeType.Trigger:
            case NodeType.Scheduler:
              return TerminalNodeComponent;
            case NodeType.HttpAction:
              return ActionNodeComponent;
            case NodeType.Condition:
            case NodeType.Switch:
            case NodeType.Loop:
            case NodeType.Wait:
            case NodeType.DtoTransformation:
            case NodeType.UrlEncoding.toLowerCase():
            case NodeType.Uuid.toLowerCase():
            case NodeType.DtoSet.toLowerCase():
            case NodeType.Gateway:
            case NodeType.Notify:
            case NodeType.HttpResponse:
            case NodeType.UserCache.toLowerCase():
            case NodeType.HttpRequest.toLowerCase():
            case NodeType.EneqtoData.toLowerCase():
              return ConditionNodeComponent;
            case NodeType.Annotation:
              return AnnotationNodeComponent;
            default:
              return NodeComponent;
          }
        },
        connection: (data: RenderPayload<'connection'>) => {
          if (data.payload.isMagnetic) {
            return MagneticConnectionComponent;
          }

          return CustomConnectionComponent;
        },
        socket: (socket) => {
          switch (socket.side) {
            case 'input':
              return DefaultInputSocketComponent;
            case 'output':
              return DefaultOutputSocketComponent;
            default:
              return CustomSocketComponent;
          }
        },
      },
    });
  }

  #initAreaExtensions(): void {
    AreaExtensions.restrictor(this.area, {
      scaling: () => {
        return { min: this.#minZoom, max: this.#maxZoom };
      },
    });
    AreaExtensions.snapGrid(this.area, {
      size: 20,
    });

    this.#accumulateOnCtrl = accumulateOnCtrl(this.nodeEditorElement.nativeElement);
    const selector = AreaExtensions.selector();

    this.selectableNodes = AreaExtensions.selectableNodes(this.area, selector, {
      accumulating: this.#accumulateOnCtrl,
    });

    AreaExtensions.simpleNodesOrder(this.area);
    HistoryExtensions.keyboard(this.history);
  }

  #handleNodeClick(): void {
    this.area.addPipe((context) => {
      switch (context.type) {
        case 'translated': {
          this.area.container.style.backgroundPosition = `left ${context.data.position.x}px top ${context.data.position.y}px`;

          break;
        }
        default:
          break;
      }

      return context;
    });
  }

  #initMagneticConnection(): void {
    connectionUtilsProxy.useMagneticConnection(this.connection, {
      async createConnection(from, to, editor) {
        if (from.side === to.side) return;
        const [source, target] = from.side === 'output' ? [from, to] : [to, from];
        const sourceNode = editor.getNode(source.nodeId);
        const targetNode = editor.getNode(target.nodeId);

        await editor.addConnection(
          new ClassicPreset.Connection(sourceNode, source.key as never, targetNode, target.key as never),
        );
      },
      display(from, to) {
        return from.side !== to.side;
      },
      offset(socket, position) {
        const socketRadius = 10;

        return {
          x: position.x + (socket.side === 'input' ? -socketRadius : socketRadius),
          y: position.y,
        };
      },
    });
  }

  #setNodeEditorStyles(direction: ZoomDirection, zoomLevel?: number): void {
    const zoomStep = zoomLevel ? zoomLevel : direction === 'in' ? 0.1 : -0.1;
    const newWidth = this.#backgroundImageSize + zoomStep * 20;
    const dotWidth = newWidth / 2;

    this.#backgroundImageSize = newWidth;
    this.area.container.style.backgroundImage = `radial-gradient(circle at ${dotWidth.toFixed(2)}px ${dotWidth.toFixed(2)}px, var(--color-canvas-dot) 1px, transparent 0)`;
    this.area.container.style.backgroundSize = `${newWidth.toFixed(2)}px ${newWidth.toFixed(2)}px`;
  }

  #getFlowWorkerNodes(): FlowWorkerNode[] {
    return this.editor
      .getNodes()
      .filter((node) => {
        return node.nodeData.type.toLowerCase() !== NodeType.Annotation.toLowerCase();
      })
      .map((node) => {
        const nodeView = this.area.nodeViews.get(node.id);

        if (!nodeView) {
          return null;
        }

        return {
          id: node.id,
          position: nodeView.position,
          width: nodeView.element.clientWidth,
          height: nodeView.element.clientHeight,
          left: nodeView.position.x,
          top: nodeView.position.y,
        };
      });
  }

  #prepareFlowData(data: Flow): FlowPayload {
    const nodes = data.nodes.map((node) => {
      // remove unused properties from node
      const {
        inputs,
        controls,
        selected,
        height,
        width,
        typeVersion,
        nodeData: nodeDataProperties,
        ...flowNode
      } = node;

      // remove unused properties from node.nodeData
      const nodeDataInputs = 'inputs';
      let httpData = null;
      const { icon, [nodeDataInputs]: inputsValue, outputs, execution, data, ...nodeDataData } = nodeDataProperties;
      const parameters = { ...nodeDataProperties.parameters };

      switch (nodeDataProperties.type) {
        case NodeType.HttpAction:
          httpData = {
            action_id: data.id,
          };
          break;
        case NodeType.HttpResponse:
          nodeDataData.parameters = ((nodeDataData.parameters as Parameter[]) || [])
            .filter((parameter) => {
              return !['content-length', 'date', 'connection'].includes(parameter.key?.toLowerCase());
            })
            .map((parameter) => {
              const { is_edit, ...params } = parameter;

              return {
                ...params,
                ...(parameter.subject === 'body' &&
                  parameter.id &&
                  parameter.parent_id && {
                    category: parameter.parent_id,
                  }),
              };
            });
          break;
        case NodeType.Trigger:
          if (nodeDataData.name.toLowerCase() === TriggerNodesEnum.Api.toLowerCase()) {
            nodeDataData.name = nodeDataData.kind;
          }
          break;
        default:
          break;
      }

      nodeDataData.name ??= node.label;

      return {
        ...flowNode,
        id: +flowNode.id as unknown as string, // BE doesn't accept string id, so we need to convert it to number
        node_data: {
          ...nodeDataData,
          data: httpData,
        },
        type_version: typeVersion,
      };
    });

    const connections = data.connections?.map((connection, index) => {
      const originalId = connection.id; // Capture before overwrite for waypoint lookup

      // BE doesn't accept string id, so we need to convert it to number
      connection.id = +(index + 1) as unknown as string;
      // remove unused properties from connection
      const { isMagnetic, ...connectionData } = connection;

      // Inject waypoints from the dedicated waypoints map (populated by A* worker or backend load)
      const waypoints = this.#connectionWaypoints[String(originalId)];

      return {
        ...connectionData,
        // BE doesn't accept string id, so we need to convert it to number
        source: +connectionData.source as unknown as string,
        target: +connectionData.target as unknown as string,
        ...(waypoints?.length && { waypoints }),
      };
    });

    // Strip dialog-only version keys so the overwrite (PUT) payload is byte-identical to pre-versioning behaviour.
    const { version_message: _vm, is_latest: _il, ...rest } = data as any;

    return {
      ...rest,
      connections,
      nodes,
      ...(data.version?.toString() && { named_version: data.version.toString() }),
      state: FlowState.FINAL, // @Todo: temporary solution, BE will accept only FINAL state for now
    };
  }

  #isSocketConnected(socket: { nodeId: string; key: string; side: 'input' | 'output' }): boolean {
    return this.editor.getConnections().some((connection) => {
      if (socket.side === 'input') {
        return connection.target === socket.nodeId && connection.targetInput === socket.key;
      }

      return connection.source === socket.nodeId && connection.sourceOutput === socket.key;
    });
  }

  async #addNodesInBatches(processedNodes: FlowNode[]): Promise<void> {
    for (let i = 0; i < processedNodes.length; i += this.#batchSize) {
      const batch = processedNodes.slice(i, i + this.#batchSize);
      const batchPromises = batch.map(async (processedNode) => {
        // Create a new FlowNode instance to avoid reference issues like lost methods (removeOutput, addOutput, etc.)
        // when adding nodes to the editor
        let flowNode = new FlowNode(processedNode.nodeData.name);

        flowNode = Object.assign(flowNode, processedNode);

        flowNode.nodeData = {
          ...flowNode.nodeData,
          execution: { executed: false, result: signal<JSONValue>(null) },
        };

        await this.editor.addNode(flowNode);
        this.nodes.update((nodes) => [...nodes, flowNode]);
      });

      await Promise.all(batchPromises);
      await this.#nextAnimationFrame();
    }
  }

  async #translateInBatches(processedNodes: FlowNode[]): Promise<void> {
    for (let i = 0; i < processedNodes.length; i += this.#batchSize) {
      const batch = processedNodes.slice(i, i + this.#batchSize);
      const translatePromises = batch.map((processedNode) =>
        this.area.translate(processedNode.id, { x: processedNode.position.x, y: processedNode.position.y }),
      );

      await Promise.all(translatePromises);
      await this.#nextAnimationFrame();
    }
  }

  #populatePathCacheFromLoadedWaypoints(processedConnections: ProcessedConnection[]): void {
    for (const connection of processedConnections) {
      if (connection.waypoints?.length) {
        // Use string key to match Rete's connection.id (always string)
        const connectionId = String(connection.id);

        // Store in pathCache (keyed by connection ID) for createAStarTransformer
        this.#pathCache[connectionId] = connection.waypoints;
        // Store in connectionWaypoints for saving back to backend
        this.#connectionWaypoints[connectionId] = connection.waypoints;
      }
    }
  }

  async #addConnectionsInBatches(processedConnections: ProcessedConnection[]): Promise<void> {
    const batchSize = 10; // Connections are heavier, so for now it will be fixed to 10

    for (let i = 0; i < processedConnections.length; i += batchSize) {
      const batch = processedConnections.slice(i, i + batchSize);
      const connectionPromises = batch.map((processedConnection) => {
        const sourceNode = this.editor.getNode(processedConnection.source);
        const targetNode = this.editor.getNode(processedConnection.target);
        const connection = new Connection(
          sourceNode,
          processedConnection.sourceOutput,
          targetNode,
          processedConnection.targetInput,
        );

        connection.id = processedConnection.id;
        connection.isMagnetic = processedConnection.isMagnetic;
        connection.waypoints = processedConnection.waypoints;

        return this.editor.addConnection(connection);
      });

      await Promise.all(connectionPromises);
      await this.#nextAnimationFrame();
    }
  }

  async #nextAnimationFrame(): Promise<void> {
    await new Promise((res) => {
      requestAnimationFrame(res);
    });
  }
}
